import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Drawer,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { font } from '../../utils/fontHelper';

export interface AdminPublishPreviewShellProps {
  open: boolean;
  onClose: () => void;
  /** Header title */
  title: string;
  children: React.ReactNode;
  onPublish: () => void;
  publishDisabled?: boolean;
  publishing?: boolean;
  publishLabel?: string;
  backLabel?: string;
}

/**
 * Bottom Drawer on mobile (md and down), centered Dialog on desktop.
 */
export const AdminPublishPreviewShell: React.FC<AdminPublishPreviewShellProps> = ({
  open,
  onClose,
  title,
  children,
  onPublish,
  publishDisabled,
  publishing,
  publishLabel = 'Publish',
  backLabel = 'Go back',
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const actions = (
    <Box
      sx={{
        display: 'flex',
        gap: 1.5,
        width: '100%',
        p: 2,
        pt: 1.5,
        pb: `max(16px, env(safe-area-inset-bottom, 16px))`,
        flexShrink: 0,
        borderTop: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.background.paper,
      }}
    >
      <Button
        fullWidth
        variant="outlined"
        onClick={onClose}
        disabled={!!publishing}
        sx={{ fontFamily: font(), textTransform: 'none', fontWeight: 600 }}
      >
        {backLabel}
      </Button>
      <Button
        fullWidth
        variant="contained"
        onClick={onPublish}
        disabled={publishDisabled || publishing}
        sx={{ fontFamily: font(), textTransform: 'none', fontWeight: 600 }}
      >
        {publishing ? 'Publishing…' : publishLabel}
      </Button>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        ModalProps={{ keepMounted: false }}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: '92vh',
            overflow: 'hidden',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: '92vh' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1.5,
              borderBottom: 1,
              borderColor: 'divider',
              flexShrink: 0,
            }}
          >
            <Typography sx={{ fontFamily: font(), fontWeight: 700, fontSize: 17 }}>{title}</Typography>
            <IconButton onClick={onClose} aria-label="Close" disabled={!!publishing} size="small">
              <Close />
            </IconButton>
          </Box>
          <Box sx={{ overflow: 'auto', flex: 1, minHeight: 0, px: 2, py: 2 }}>{children}</Box>
          {actions}
        </Box>
      </Drawer>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!publishing) onClose();
      }}
      maxWidth="sm"
      fullWidth
      scroll="paper"
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pr: 1,
          fontFamily: font(),
          fontWeight: 700,
        }}
      >
        {title}
        <IconButton onClick={onClose} aria-label="Close" disabled={!!publishing} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 2 }}>
        {children}
      </DialogContent>
      <DialogActions sx={{ p: 0, display: 'block' }}>{actions}</DialogActions>
    </Dialog>
  );
};
